import { useEffect, useRef, useState } from 'react';
import {
  AssetManifestClient,
  BiomePackManager,
  buildPackFromTheme,
  mountSlotUnlockBoard,
  slotUnlockMode,
  type PackProgress,
  type PlayerProfile,
  type SlotUnlockArtUrls,
  type SlotUnlockHandle,
  type SlotUnlockPress,
  type SlotUnlockView,
  type Theme,
} from '@goodtime/engine';
import {
  type WorldMapNode,
  slotGemPrice,
} from '@goodtime/shared';
import checkUrl from '../assets/icon/check.png';
import gemUrl from '../assets/icon/gem.png';
import lockUrl from '../assets/icon/lock.png';
import trackUrl from '../assets/board/gauge_track.png';
import fillUrl from '../assets/board/gauge_fill.png';
import floorUrl from '../assets/reel/forest_column_bg.png';
import { BUTTON_PLATES } from '../themes/roadGames';

const ART: SlotUnlockArtUrls = { floor: floorUrl, gem: gemUrl, lock: lockUrl, check: checkUrl, track: trackUrl, fill: fillUrl };

/**
 * T1 gem economy (2026-05-28): the slot purchase board.
 *
 * <p>Opens when a player taps a locked slot in the world map, or unasked
 * when they can afford one (the offer). Shows the slot, the flat
 * {@link SLOT_UNLOCK_PRICE_GEMS} price and the player's gems towards it.
 * On a buy: the {@link PlayerProfile} spends the gems and emits
 * {@code slot-unlocked} (which {@code useSlotUnlockSync} sends to the
 * server), the slot's art downloads if it isn't here yet, then
 * {@code onUnlocked(node)} lets the host route to the slot and the board
 * closes.
 *
 * <p>Short of gems, it says how many more and where gems come from, with a
 * way back to play. There's no gem store in v1.
 *
 * <p>Drawn in Pixi on the board kit (engine `widgets/unlock`), on its own
 * canvas over the slot or the map (producer 2026-10-14: every dialog in
 * Pixi, painted).
 */
export interface SlotUnlockSheetProps {
  /** The world-map node the player tapped. The board looks up its
   *  price via {@link slotGemPrice} and renders accordingly. */
  node: WorldMapNode;
  profile: PlayerProfile;
  /** Resolved theme this node maps to: drives the
   *  {@link BiomePackManager} pack-download step that runs after the
   *  gem-unlock succeeds. {@code null} skips the download step and
   *  routes immediately (e.g. for engine-bundled themes that don't
   *  ship per-theme assets). */
  theme?: Theme | null;
  /** Called on a successful unlock so the host can auto-route to the
   *  newly-unlocked scene. The board still closes itself after. */
  onUnlocked: (node: WorldMapNode) => void;
  /** Called when the player dismisses the board without buying. */
  onClose: () => void;
  /** Opened unasked because the player can afford a new slot, rather than
   *  from a tap on a locked one: says so, with a way to say "not now". */
  offer?: boolean;
  /** Gems land on the player's reels (`reel_gems`): the hint says so. */
  reelGems?: boolean;
}

export function SlotUnlockSheet({
  node,
  profile,
  theme,
  onUnlocked,
  onClose,
  offer = false,
  reelGems = false,
}: SlotUnlockSheetProps) {
  // Re-render on gem-changed so the gems shown and the afford state stay
  // in sync if a concurrent grant lands while the board is open (e.g. a
  // wheel-match payout finishes).
  const [, setVersion] = useState(0);
  useEffect(() => {
    const refresh = (): void => setVersion((v) => v + 1);
    const sub = profile.events.on('gems-changed', refresh);
    return () => { sub.dispose(); };
  }, [profile]);

  // Stage 2: the pack-download state machine. 'idle' → 'downloading' →
  // 'ready' → routes to game. The board can't be dismissed in
  // 'downloading' and 'ready'; its gauge follows
  // {@link BiomePackManager#onProgress}.
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'ready'>(
    'idle',
  );
  const [progress, setProgress] = useState<PackProgress | null>(null);

  const price = slotGemPrice(node.id);
  // Free slot (defensive): the world map shouldn't open this for a free
  // node, but unlock it so the player isn't stuck.
  const free = price === 0;
  useEffect(() => {
    if (!free) return;
    profile.unlockSlot(String(node.id), 0);
    if (theme) BiomePackManager.shared.markReady(theme.id);
    onUnlocked(node);
    // Once, for this node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [free, node.id]);

  const canAfford = price !== null && profile.gems >= price;

  const handleBuy = (): void => {
    if (price === null || !canAfford || downloadState !== 'idle') return;
    const ok = profile.unlockSlot(String(node.id), price);
    if (!ok) return;

    // No theme provided OR pack already cached → skip download step
    // and route. {@link markReady} is a no-op for already-ready packs.
    if (!theme || BiomePackManager.shared.isPackReady(theme.id)) {
      if (theme) BiomePackManager.shared.markReady(theme.id);
      onUnlocked(node);
      onClose();
      return;
    }

    // Start downloading. The progress listener fires repeatedly as
    // bytes arrive; the {@link startDownload} promise resolves once
    // every URL in the pack is cached.
    setDownloadState('downloading');
    const themeId = theme.id;
    const pack = buildPackFromTheme(theme);
    const unsubscribe = BiomePackManager.shared.onProgress(themeId, setProgress);
    void BiomePackManager.shared
      .startDownload(themeId, pack)
      .then(() => {
        unsubscribe();
        setDownloadState('ready');
        // Brief "Ready!" hold so the player sees the success state
        // before the slot scene takes over. 800ms feels right:
        // long enough to register, short enough to feel snappy.
        setTimeout(() => {
          onUnlocked(node);
          onClose();
        }, 800);
      })
      .catch((err: unknown) => {
        // Stage 2 should never hit this branch (every URL is local).
        // Stage 3 wires retry + offline UI here.
        console.error('[SlotUnlockSheet] pack download failed', err);
        unsubscribe();
        setDownloadState('idle');
      });
  };

  const view: SlotUnlockView = {
    mode: slotUnlockMode({ price, gems: profile.gems, offer, download: downloadState }),
    name: node.name,
    price: price ?? 0,
    gems: profile.gems,
    reelGems,
    progress: progress
      ? { filesLoaded: progress.filesLoaded, filesTotal: progress.filesTotal, bytesLoaded: progress.bytesLoaded, bytesTotal: progress.bytesTotal }
      : null,
  };

  // The board's callbacks read the latest render's handlers.
  const pressRef = useRef<(press: SlotUnlockPress) => void>(() => {});
  pressRef.current = (press) => {
    if (press === 'buy') handleBuy();
    else if (downloadState === 'idle') onClose();
  };
  const viewRef = useRef(view);
  viewRef.current = view;

  const host = useRef<HTMLDivElement>(null);
  const board = useRef<SlotUnlockHandle | null>(null);
  const picture = theme ? AssetManifestClient.shared.resolveCdnUrl(`map_node/${theme.id}_reels.png`) : null;
  const shown = !free;
  useEffect(() => {
    if (!shown || !host.current) return;
    const handle = mountSlotUnlockBoard(host.current, {
      art: ART,
      plates: BUTTON_PLATES,
      view: viewRef.current,
      pictureUrl: picture,
      onPress: (press) => pressRef.current(press),
    });
    board.current = handle;
    return () => {
      handle.destroy();
      board.current = null;
    };
    // One board per node: the view follows below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, node.id]);
  const viewKey = JSON.stringify(view);
  useEffect(() => {
    board.current?.show(viewRef.current);
  }, [viewKey]);

  if (!shown) return null;
  return (
    <div
      ref={host}
      role="dialog"
      aria-modal="true"
      aria-label={view.mode === 'offer' ? 'New slot' : 'Unlock slot'}
      data-testid="slot-unlock"
      style={{ position: 'fixed', inset: 0, zIndex: 250 }}
    />
  );
}
